import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { addDeliveryZone } from '../store/slices/pricingSlice';
import { addToast } from '../store/slices/toastsSlice';
import { v4 as uuidv4 } from 'uuid';

interface CreateZoneModalProps {
  onClose: () => void;
}

const CreateZoneModal: React.FC<CreateZoneModalProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const clients = useSelector((state: RootState) => state.clients.clients);

  const [selectedTowns, setSelectedTowns] = useState<string[]>([]);
  const [manualTownInput, setManualTownInput] = useState('');
  const [deliveryPrice, setDeliveryPrice] = useState('');

  // Extract unique towns from client addresses
  const availableTowns = useMemo(() => {
    const townsSet = new Set<string>();

    clients.forEach(client => {
      if (client.address) {
        // Split address by commas and try to extract city/town
        const parts = client.address.split(',').map(p => p.trim());
        // Typically the second part is the city/town (after street address)
        if (parts.length >= 2) {
          const town = parts[1].trim();
          if (town) {
            townsSet.add(town);
          }
        }
      }
    });

    return Array.from(townsSet).sort();
  }, [clients]);

  const handleTownSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const town = e.target.value;
    if (town && !selectedTowns.includes(town)) {
      setSelectedTowns([...selectedTowns, town]);
    }
  };

  const handleAddManualTown = () => {
    const town = manualTownInput.trim();
    if (town && !selectedTowns.includes(town)) {
      setSelectedTowns([...selectedTowns, town]);
      setManualTownInput('');
    }
  };

  const handleRemoveTown = (townToRemove: string) => {
    setSelectedTowns(selectedTowns.filter(town => town !== townToRemove));
  };

  const handleManualTownKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddManualTown();
    }
  };

  const handleDeliveryPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, digits, and one decimal point with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setDeliveryPrice(value);
    }
  };

  const handleSave = () => {
    if (selectedTowns.length === 0) {
      dispatch(addToast('Please add at least one town to the zone', 'error'));
      return;
    }

    const parsedPrice = parseFloat(deliveryPrice);
    if (deliveryPrice === '' || isNaN(parsedPrice) || parsedPrice < 0) {
      dispatch(addToast('Please enter a valid delivery price', 'error'));
      return;
    }

    const newZone = {
      id: uuidv4(),
      towns: selectedTowns,
      deliveryPrice: parsedPrice
    };

    dispatch(addDeliveryZone(newZone));
    dispatch(addToast('Delivery zone created successfully!', 'success'));
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Create New Zone</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Zone:</label>

            {/* Dropdown for existing towns */}
            <select value="" onChange={handleTownSelect} style={styles.select}>
              <option value="">Select a town from clients...</option>
              {availableTowns.map(town => (
                <option key={town} value={town}>
                  {town}
                </option>
              ))}
            </select>

            {/* Manual town input */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <input
                type="text"
                value={manualTownInput}
                onChange={e => setManualTownInput(e.target.value)}
                onKeyDown={handleManualTownKeyDown}
                style={{ ...styles.input, flex: 1 }}
                placeholder="Or type a town name..."
              />
              <button onClick={handleAddManualTown} style={styles.addButton} type="button">
                Add
              </button>
            </div>

            {/* Selected towns list */}
            {selectedTowns.length > 0 && (
              <div style={styles.townsList}>
                {selectedTowns.map(town => (
                  <div key={town} style={styles.townTag}>
                    <span>{town}</span>
                    <button onClick={() => handleRemoveTown(town)} style={styles.removeTownButton} type="button">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="deliveryPrice" style={styles.label}>
              Delivery Price:
            </label>
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <span style={styles.dollarSign}>$</span>
              <input
                id="deliveryPrice"
                type="text"
                value={deliveryPrice}
                onChange={handleDeliveryPriceChange}
                style={{ ...styles.input, paddingLeft: '28px' }}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button style={styles.saveButton} onClick={handleSave}>
            Save Zone
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '30px 20px'
  },
  formGroup: {
    marginBottom: '25px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '8px'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  townsList: {
    marginTop: '15px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  townTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#1976d2'
  },
  removeTownButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#1976d2',
    cursor: 'pointer',
    padding: 0,
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1'
  },
  dollarSign: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666',
    fontSize: '16px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default CreateZoneModal;
